import { transformExtent } from "ol/proj";
import { fromExtent } from "ol/geom/Polygon";
import { LinearRing } from "ol/geom";

export const centralEuropeExtent = transformExtent(
  [
    -19.98902381657924,
    32.750667412687676,
    29.840756983321853,
    62.440050453973896,
  ],
  "EPSG:4326",
  "EPSG:3857",
); // XXX change/sync to backend

/**
 * Where live radar exists at all.
 *
 * The bounds of the DWD composite's tile pyramid. The radar layer clips itself
 * to exactly this, so it is the honest answer to "is there radar here", and
 * dwd.ts reads it from here instead of writing it out twice.
 *
 * Rectangular on purpose. The real coverage is the shape radarCoverageInv
 * masks, which is smaller; a viewport inside this box but outside that shape
 * shows an empty radar rather than a wrong claim, and erring that way is much
 * better than telling someone over Munich that their country is unsupported.
 */
export const dwdRadarExtent4326: [number, number, number, number] = [2.8125, 45, 19.6875, 56.25];

export const dwdRadarExtent = transformExtent(dwdRadarExtent4326, "EPSG:4326", "EPSG:3857");

/**
 * Where the Swiss reflectivity composite exists at all.
 *
 * Matches `openradar.SWITZERLAND.bbox` on the backend exactly: the grid
 * that composite builds and this extent clips to are the same rectangle, so
 * moving one without the other would either clip real tiles or claim
 * coverage the composite never renders.
 *
 * Rectangular for the same reason DWD's is: it is the grid's edge, not a
 * coverage claim. The claim is the circles in `radarCoverageInv`, and this
 * box is sized to contain them.
 */
export const chRadarExtent4326: [number, number, number, number] = [2.8, 43.7, 13.1, 49.6];

export const chRadarExtent = transformExtent(chRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the French composite exists at all: `openradar.FRANCE.bbox`, as above. */
export const frRadarExtent4326: [number, number, number, number] = [-8.1, 39.6, 12.7, 52.5];

export const frRadarExtent = transformExtent(frRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Czech composite exists at all: `openradar.CZECHIA.bbox`, as above. */
export const czRadarExtent4326: [number, number, number, number] = [10.1, 47.1, 20.5, 52.1];

export const czRadarExtent = transformExtent(czRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Polish composite exists at all: `openradar.POLAND.bbox`, as above. */
export const plRadarExtent4326: [number, number, number, number] = [12.0, 47.8, 25.6, 56.8];

export const plRadarExtent = transformExtent(plRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Belgian composite exists at all: `openradar.BELGIUM.bbox`, as above. */
export const beRadarExtent4326: [number, number, number, number] = [-0.6, 47.6, 9.1, 53.5];

export const beRadarExtent = transformExtent(beRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Slovak composite exists at all: `openradar.SLOVAKIA.bbox`, as above. */
export const skRadarExtent4326: [number, number, number, number] = [13.8, 46.0, 24.3, 51.5];

export const skRadarExtent = transformExtent(skRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Lithuanian composite exists at all: `openradar.LITHUANIA.bbox`, as above. */
export const ltRadarExtent4326: [number, number, number, number] = [18.2, 52.3, 29.1, 57.9];

export const ltRadarExtent = transformExtent(ltRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Estonian composite exists at all: `openradar.ESTONIA.bbox`, as above. */
export const eeRadarExtent4326: [number, number, number, number] = [20.1, 56.2, 29.9, 61.7];

export const eeRadarExtent = transformExtent(eeRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Irish composite exists at all: `openradar.IRELAND.bbox`, as above. */
export const ieRadarExtent4326: [number, number, number, number] = [-12.7, 50.4, -2.4, 55.7];

export const ieRadarExtent = transformExtent(ieRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Croatian composite exists at all: `openradar.CROATIA.bbox`, as above. */
export const hrRadarExtent4326: [number, number, number, number] = [10.9, 40.6, 21.9, 48.2];

export const hrRadarExtent = transformExtent(hrRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Danish composite exists at all: `openradar.DENMARK.bbox`, as above. */
export const dkRadarExtent4326: [number, number, number, number] = [4.8, 52.9, 18.7, 59.7];

export const dkRadarExtent = transformExtent(dkRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Dutch composite exists at all: `openradar.NETHERLANDS.bbox`, as above. */
export const nlRadarExtent4326: [number, number, number, number] = [1.0, 49.5, 8.8, 55.3];

export const nlRadarExtent = transformExtent(nlRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Finnish composite exists at all: `openradar.FINLAND.bbox`, as above. */
export const fiRadarExtent4326: [number, number, number, number] = [17.0, 57.8, 34.7, 70.8];

export const fiRadarExtent = transformExtent(fiRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Swedish composite exists at all: `openradar.SWEDEN.bbox`, as above. */
export const seRadarExtent4326: [number, number, number, number] = [8.8, 54.0, 27.2, 70.0];

export const seRadarExtent = transformExtent(seRadarExtent4326, "EPSG:4326", "EPSG:3857");

/** Where the Norwegian composite exists at all: `openradar.NORWAY.bbox`, as above. */
export const noRadarExtent4326: [number, number, number, number] = [0.4, 56.2, 35.5, 72.8];

export const noRadarExtent = transformExtent(noRadarExtent4326, "EPSG:4326", "EPSG:3857");

/**
 * Everywhere there is *no* radar: the world, with the covered area cut out.
 *
 * The dark wash the map draws over unsupported regions is this polygon's fill,
 * so the hole is the claim "there is radar here" and the fill is "there is
 * not". It holds every network: what each one's antennas actually reach, not
 * the rectangles their grids are stored on.
 *
 * DWD's part is its published outline, which measures as a union of 150 km
 * circles around its seventeen sites. MeteoSwiss's is 246 km circles around
 * its five, Meteo-France's 256 km around its twenty-four, CHMI's 260 km
 * around its two, IMGW's 250 km around its ten and SHMÚ's 240 km around its
 * four. RMI's differ by site: 200 km round Helchteren, 250 round Jabbeke and
 * Wideumont; so do Met Éireann's, 250 round Dublin and 248.5 round Shannon.
 * The rest run 238 km (DMI) to 250 (KNMI, FMI), `radarSites.ts` has each.
 * Each is the range the network's own files carry as `nbins * rscale` on
 * their lowest tilt, read no further than 250 km.
 *
 * The networks are merged into one hole per part instead of one hole each.
 * The shapes overlap, and overlapping interior rings do not punch several
 * holes: under the nonzero winding rule the overlap counts back to filled,
 * so exactly the ground covered twice would have been shaded as having none.
 * Parts that touch nowhere are a hole each, which is safe, as they do not
 * overlap: the continent, and Ireland, whose radars reach no other
 * network's (Dublin is 679 km from Jabbeke, and the middle of Britain is no
 * network's). Inside the continent four islands no radar reaches though
 * radars ring them are rings of their own, which count back to filled for
 * the same reason: the Niedere Tauern, the northern Apennines, the Baltic
 * between Gotland, Latvia and Estonia, and the fells west of Arjeplog.
 *
 * Generated by `scripts/radar-wash.py`, which draws the circles the way the
 * backend's coverage document does and simplifies to 200 m, far under a pixel at the zooms this
 * is drawn at. Run it again when a network or a site's reach changes.
 */
export const radarCoverageInv = fromExtent(
  transformExtent([-180, -90, 180, 90], "EPSG:4326", "EPSG:3857"),
);
// A hole: the continent, every network but Ireland's.
radarCoverageInv.appendLinearRing(
  new LinearRing([
    [523194.100000, 5021259.800000],
    [513471.000000, 5014475.800000],
    [498484.000000, 5004936.800000],
    [483048.600000, 4996181.500000],
    [467204.600000, 4988229.700000],
    [456434.700000, 4983383.800000],
    [439999.400000, 4976809.700000],
    [423264.200000, 4971082.500000],
    [406271.100000, 4966214.800000],
    [394820.000000, 4963452.600000],
    [377491.300000, 4960040.400000],
    [360018.200000, 4957512.200000],
    [342444.000000, 4955873.500000],
    [324812.000000, 4955127.900000],
    [307165.400000, 4955277.100000],
    [289547.800000, 4956320.600000],
    [272002.500000, 4958256.400000],
    [254572.800000, 4961079.900000],
    [243038.400000, 4963452.600000],
    [225896.900000, 4967741.200000],
    [214594.200000, 4971082.500000],
    [197859.100000, 4976809.700000],
    [186866.300000, 4981099.200000],
    [170653.900000, 4988229.700000],
    [160047.700000, 4993440.700000],
    [144471.700000, 5001930.300000],
    [134327.100000, 5008030.400000],
    [119498.200000, 5017825.900000],
    [109887.600000, 5024776.700000],
    [95912.700000, 5035816.200000],
    [86906.100000, 5043573.700000],
    [78160.000000, 5051640.200000],
    [67357.400000, 5062425.500000],
    [47832.400000, 5067927.600000],
    [31476.300000, 5073509.400000],
    [8867.800000, 5067667.400000],
    [-2790.800000, 5065271.600000],
    [-14520.500000, 5063271.500000],
    [-32220.100000, 5061017.500000],
    [-44071.200000, 5060014.400000],
    [-55948.200000, 5059412.200000],
    [-67838.100000, 5059211.400000],
    [-79728.000000, 5059412.200000],
    [-97534.600000, 5060465.900000],
    [-109367.800000, 5061669.100000],
    [-121155.700000, 5063271.500000],
    [-138724.400000, 5066420.200000],
    [-150342.700000, 5069013.000000],
    [-161870.700000, 5071997.900000],
    [-178965.800000, 5077204.000000],
    [-190212.800000, 5081155.500000],
    [-201325.500000, 5085487.200000],
    [-217715.500000, 5092688.200000],
    [-228438.400000, 5097950.800000],
    [-238984.000000, 5103577.100000],
    [-254444.200000, 5112685.800000],
    [-264494.900000, 5119195.700000],
    [-274327.200000, 5126047.900000],
    [-288641.500000, 5136952.800000],
    [-302401.100000, 5148587.500000],
    [-311248.000000, 5156736.300000],
    [-324002.600000, 5169526.900000],
    [-332146.700000, 5178419.500000],
    [-343799.600000, 5192283.400000],
    [-351178.600000, 5201862.000000],
    [-361639.200000, 5216708.600000],
    [-368194.800000, 5226910.000000],
    [-377379.200000, 5242640.400000],
    [-383057.700000, 5253396.400000],
    [-390889.300000, 5269904.500000],
    [-395642.300000, 5281141.800000],
    [-402052.300000, 5298314.300000],
    [-405836.800000, 5309955.100000],
    [-409223.200000, 5321734.500000],
    [-412206.100000, 5333639.100000],
    [-415912.800000, 5351701.100000],
    [-418685.000000, 5369967.100000],
    [-420509.700000, 5388389.200000],
    [-421376.400000, 5406918.200000],
    [-421276.900000, 5425504.200000],
    [-420205.800000, 5444096.500000],
    [-418159.900000, 5462643.800000],
    [-415819.900000, 5477349.300000],
    [-419949.400000, 5489274.100000],
    [-425312.000000, 5507236.600000],
    [-429749.800000, 5525489.200000],
    [-432185.700000, 5537795.700000],
    [-435044.500000, 5556427.100000],
    [-436414.300000, 5568941.200000],
    [-437351.200000, 5581512.700000],
    [-437852.400000, 5594126.600000],
    [-437782.000000, 5613094.000000],
    [-437184.300000, 5625747.500000],
    [-436144.500000, 5638389.800000],
    [-434662.200000, 5651005.400000],
    [-431609.000000, 5669844.300000],
    [-429021.400000, 5682324.000000],
    [-425993.500000, 5694721.800000],
    [-421449.300000, 5710493.700000],
    [-433925.300000, 5721030.600000],
    [-443367.400000, 5729663.500000],
    [-456988.000000, 5743218.100000],
    [-465690.700000, 5752644.700000],
    [-478151.700000, 5767345.800000],
    [-486048.400000, 5777506.200000],
    [-497252.600000, 5793260.000000],
    [-505131.200000, 5805475.300000],
    [-518888.900000, 5806145.600000],
    [-538144.500000, 5807926.200000],
    [-557291.900000, 5810673.500000],
    [-576284.700000, 5814381.900000],
    [-595077.200000, 5819043.500000],
    [-613623.500000, 5824648.600000],
    [-631878.400000, 5831185.200000],
    [-643864.200000, 5836053.800000],
    [-661534.000000, 5844111.500000],
    [-678793.900000, 5853059.300000],
    [-695600.500000, 5862878.200000],
    [-706532.000000, 5869897.600000],
    [-722489.000000, 5881120.000000],
    [-737880.800000, 5893152.400000],
    [-752667.200000, 5905968.300000],
    [-762169.000000, 5914933.600000],
    [-775859.500000, 5928991.000000],
    [-788842.300000, 5943751.700000],
    [-801081.200000, 5959182.000000],
    [-808809.900000, 5969823.400000],
    [-819732.300000, 5986289.700000],
    [-826552.800000, 5997586.900000],
    [-836068.900000, 6014982.200000],
    [-841923.800000, 6026860.800000],
    [-849951.500000, 6045069.500000],
    [-854788.800000, 6057450.000000],
    [-861254.500000, 6076348.700000],
    [-865028.200000, 6089146.300000],
    [-869867.600000, 6108604.300000],
    [-872538.100000, 6121729.300000],
    [-875697.100000, 6141608.900000],
    [-877232.000000, 6154967.500000],
    [-878305.100000, 6168392.300000],
    [-878913.200000, 6181867.500000],
    [-878946.800000, 6202139.400000],
    [-878380.400000, 6215669.600000],
    [-877340.900000, 6229193.000000],
    [-874892.900000, 6249428.700000],
    [-872667.900000, 6262861.700000],
    [-869969.100000, 6276228.900000],
    [-865035.100000, 6296119.000000],
    [-859044.000000, 6315764.800000],
    [-852005.600000, 6335108.500000],
    [-846737.800000, 6347807.800000],
    [-841014.800000, 6360330.000000],
    [-831589.000000, 6378744.300000],
    [-821172.100000, 6396665.300000],
    [-809788.500000, 6414037.000000],
    [-801675.900000, 6425285.700000],
    [-793154.900000, 6436249.900000],
    [-779628.800000, 6452128.300000],
    [-765241.300000, 6467279.600000],
    [-750030.800000, 6481653.800000],
    [-734039.400000, 6495203.400000],
    [-722966.700000, 6503755.600000],
    [-711580.800000, 6511908.200000],
    [-699896.400000, 6519648.700000],
    [-687928.600000, 6526965.400000],
    [-675693.100000, 6533847.200000],
    [-663206.200000, 6540283.300000],
    [-650484.300000, 6546264.000000],
    [-630998.700000, 6554360.700000],
    [-617765.300000, 6559163.400000],
    [-604358.600000, 6563481.200000],
    [-590796.900000, 6567307.200000],
    [-577099.000000, 6570635.400000],
    [-563283.800000, 6573460.600000],
    [-549370.600000, 6575778.300000],
    [-535378.600000, 6577584.700000],
    [-521327.500000, 6578877.100000],
    [-507236.800000, 6579653.400000],
    [-493126.400000, 6579912.300000],
    [-479016.000000, 6579653.400000],
    [-464925.400000, 6578877.100000],
    [-450874.200000, 6577584.700000],
    [-436882.300000, 6575778.300000],
    [-416047.900000, 6572111.200000],
    [-402289.000000, 6569033.900000],
    [-388656.900000, 6565456.100000],
    [-375170.400000, 6561383.400000],
    [-361848.100000, 6556822.200000],
    [-348708.100000, 6551779.800000],
    [-335768.500000, 6546264.000000],
    [-323046.700000, 6540283.300000],
    [-307095.200000, 6531939.900000],
    [-291439.400000, 6548534.200000],
    [-276086.900000, 6563057.400000],
    [-259944.400000, 6576748.100000],
    [-243057.300000, 6589560.100000],
    [-225473.900000, 6601450.100000],
    [-207245.600000, 6612377.000000],
    [-188426.100000, 6622303.300000],
    [-175579.200000, 6628347.600000],
    [-164495.300000, 6633097.500000],
    [-159100.700000, 6645547.400000],
    [-152972.200000, 6658432.000000],
    [-146379.200000, 6671109.000000],
    [-139328.200000, 6683560.800000],
    [-131826.300000, 6695770.100000],
    [-123881.300000, 6707719.800000],
    [-111151.900000, 6725120.200000],
    [-102138.200000, 6736346.900000],
    [-92714.300000, 6747255.600000],
    [-82891.300000, 6757830.500000],
    [-72680.900000, 6768055.900000],
    [-62095.400000, 6777916.600000],
    [-51147.800000, 6787397.900000],
    [-33143.700000, 6801561.100000],
    [-27043.700000, 6817376.900000],
    [-21405.500000, 6830423.200000],
    [-15297.800000, 6843280.400000],
    [-8726.400000, 6855930.900000],
    [1986.100000, 6874480.900000],
    [9687.500000, 6886538.400000],
    [17827.300000, 6898328.700000],
    [26396.500000, 6909834.800000],
    [40034.500000, 6926525.200000],
    [49634.100000, 6937249.200000],
    [59626.700000, 6947632.400000],
    [70000.300000, 6957659.500000],
    [80742.300000, 6967315.400000],
    [91839.400000, 6976585.600000],
    [103277.600000, 6985456.200000],
    [119211.400000, 6996741.000000],
    [120059.000000, 7014289.600000],
    [122164.300000, 7036051.300000],
    [125418.100000, 7057730.400000],
    [128225.700000, 7072106.800000],
    [133392.700000, 7093510.800000],
    [137472.200000, 7107646.100000],
    [144537.300000, 7128601.200000],
    [149873.200000, 7142379.000000],
    [158805.000000, 7162710.500000],
    [165370.700000, 7176014.400000],
    [176120.600000, 7195548.300000],
    [183877.900000, 7208262.900000],
    [196380.000000, 7226827.800000],
    [205279.200000, 7238840.400000],
    [219449.900000, 7256269.500000],
    [229429.400000, 7267470.800000],
    [245167.800000, 7283603.900000],
    [261791.200000, 7298886.200000],
    [279253.500000, 7313265.200000],
    [297505.300000, 7326691.300000],
    [316493.600000, 7339117.500000],
    [336162.300000, 7350500.000000],
    [356452.700000, 7360798.200000],
    [370294.500000, 7367042.900000],
    [391482.300000, 7375453.600000],
    [413121.700000, 7382690.000000],
    [435144.800000, 7388725.500000],
    [450005.700000, 7392071.000000],
    [472513.000000, 7396058.100000],
    [495215.300000, 7398795.100000],
    [518040.000000, 7400272.000000],
    [536062.500000, 7400527.900000],
    [536242.700000, 7420779.500000],
    [536988.400000, 7435362.900000],
    [538245.300000, 7449938.500000],
    [540014.900000, 7464488.200000],
    [543631.700000, 7486223.800000],
    [546684.400000, 7500628.200000],
    [550249.200000, 7514942.000000],
    [554324.600000, 7529146.600000],
    [558908.000000, 7543223.200000],
    [563996.500000, 7557153.000000],
    [569586.200000, 7570917.100000],
    [575672.600000, 7584496.900000],
    [582250.400000, 7597873.700000],
    [589313.500000, 7611028.700000],
    [596855.300000, 7623943.500000],
    [607700.300000, 7640817.500000],
    [594231.800000, 7647260.200000],
    [580806.300000, 7654236.600000],
    [561095.600000, 7665540.800000],
    [548261.300000, 7673627.100000],
    [535688.500000, 7682145.300000],
    [517350.200000, 7695715.700000],
    [505492.400000, 7705279.500000],
    [493944.600000, 7715246.900000],
    [477233.800000, 7730934.600000],
    [466519.800000, 7741870.100000],
    [456162.300000, 7753175.400000],
    [441322.100000, 7770803.400000],
    [431910.900000, 7782985.800000],
    [422899.800000, 7795499.000000],
    [410158.900000, 7814862.200000],
    [398383.400000, 7834899.300000],
    [387610.400000, 7855565.100000],
    [381002.900000, 7869668.400000],
    [374866.700000, 7884015.600000],
    [366567.200000, 7905961.000000],
    [359380.700000, 7928369.300000],
    [355222.100000, 7943538.600000],
    [352367.500000, 7955422.200000],
    [340896.100000, 7961385.000000],
    [327138.300000, 7969091.700000],
    [306970.500000, 7981512.300000],
    [287409.700000, 7994944.000000],
    [268505.000000, 8009360.400000],
    [256290.300000, 8019504.400000],
    [244402.800000, 8030064.400000],
    [227215.700000, 8046663.500000],
    [210842.400000, 8064143.400000],
    [195328.100000, 8082467.800000],
    [185484.200000, 8095134.300000],
    [176054.400000, 8108147.100000],
    [162713.000000, 8128288.200000],
    [150371.800000, 8149136.500000],
    [139069.900000, 8170645.800000],
    [128844.600000, 8192767.300000],
    [119731.500000, 8215450.400000],
    [111764.000000, 8238641.800000],
    [104973.300000, 8262286.300000],
    [99388.600000, 8286326.400000],
    [95036.000000, 8310702.500000],
    [91939.500000, 8335352.800000],
    [90583.400000, 8351907.300000],
    [89625.200000, 8376872.400000],
    [89971.900000, 8401938.000000],
    [91635.800000, 8427035.900000],
    [93481.500000, 8443751.200000],
    [97358.800000, 8468747.000000],
    [102568.800000, 8493585.900000],
    [109110.300000, 8518195.100000],
    [114208.400000, 8534437.200000],
    [122954.600000, 8558499.500000],
    [133008.100000, 8582134.300000],
    [144351.900000, 8605267.100000],
    [153547.800000, 8621963.500000],
    [147012.400000, 8631290.100000],
    [137513.800000, 8645725.100000],
    [128486.900000, 8660501.200000],
    [119944.300000, 8675604.100000],
    [111898.400000, 8691019.100000],
    [104361.400000, 8706731.200000],
    [97344.800000, 8722724.700000],
    [90860.000000, 8738983.400000],
    [84917.800000, 8755490.700000],
    [79528.700000, 8772229.500000],
    [74702.700000, 8789182.000000],
    [70449.200000, 8806330.300000],
    [66777.300000, 8823655.800000],
    [63695.400000, 8841139.300000],
    [60196.000000, 8867618.300000],
    [58622.400000, 8885410.800000],
    [57664.000000, 8903291.200000],
    [57326.300000, 8921238.500000],
    [57614.500000, 8939231.300000],
    [58532.900000, 8957247.800000],
    [60085.100000, 8975265.900000],
    [63607.800000, 9002246.600000],
    [66755.200000, 9020169.500000],
    [70542.900000, 9038014.000000],
    [77424.600000, 9064582.300000],
    [82810.800000, 9082127.500000],
    [88833.200000, 9099511.400000],
    [99052.400000, 9125231.900000],
    [110680.400000, 9150453.600000],
    [119205.600000, 9166950.700000],
    [128340.900000, 9183165.700000],
    [138078.300000, 9199074.600000],
    [148408.400000, 9214653.500000],
    [159320.900000, 9229878.600000],
    [170804.500000, 9244726.500000],
    [182846.700000, 9259174.100000],
    [201927.800000, 9280045.100000],
    [215305.400000, 9293393.000000],
    [229189.900000, 9306262.600000],
    [243564.300000, 9318632.900000],
    [258410.300000, 9330483.200000],
    [273709.000000, 9341793.900000],
    [289440.100000, 9352545.700000],
    [305582.700000, 9362720.400000],
    [330520.100000, 9376862.200000],
    [347593.700000, 9385519.400000],
    [364998.400000, 9393542.200000],
    [382709.500000, 9400916.400000],
    [400701.500000, 9407629.100000],
    [418948.100000, 9413668.100000],
    [437422.800000, 9419022.600000],
    [456098.200000, 9423683.100000],
    [474946.500000, 9427641.000000],
    [493939.700000, 9430889.200000],
    [513049.000000, 9433421.700000],
    [532245.900000, 9435234.000000],
    [551501.100000, 9436322.700000],
    [570785.600000, 9436685.800000],
    [590070.000000, 9436322.700000],
    [614469.100000, 9434797.200000],
    [618539.600000, 9449052.700000],
    [624468.200000, 9467492.400000],
    [631068.600000, 9485755.100000],
    [638337.500000, 9503815.400000],
    [646270.200000, 9521647.400000],
    [654861.300000, 9539225.400000],
    [664104.000000, 9556523.700000],
    [673990.500000, 9573516.400000],
    [684512.100000, 9590178.000000],
    [695658.700000, 9606483.000000],
    [713525.900000, 9630216.400000],
    [726183.700000, 9645519.700000],
    [739422.600000, 9660379.100000],
    [753227.400000, 9674770.500000],
    [763390.200000, 9684685.800000],
    [763511.700000, 9703234.300000],
    [764330.100000, 9723300.800000],
    [765854.800000, 9743383.400000],
    [768089.800000, 9763457.100000],
    [771038.600000, 9783496.700000],
    [774703.400000, 9803476.200000],
    [781546.500000, 9833275.500000],
    [787006.000000, 9852989.700000],
    [793182.500000, 9872550.900000],
    [803786.800000, 9901546.200000],
    [811744.300000, 9920605.500000],
    [820406.800000, 9939416.000000],
    [829768.200000, 9957950.000000],
    [837109.800000, 9971366.700000],
    [828973.300000, 9982995.800000],
    [817503.800000, 10000455.700000],
    [806593.400000, 10018333.200000],
    [796257.700000, 10036612.300000],
    [786512.400000, 10055276.400000],
    [777372.800000, 10074308.500000],
    [764831.400000, 10103507.100000],
    [757270.200000, 10123379.500000],
    [750365.000000, 10143554.600000],
    [741266.800000, 10174340.600000],
    [736059.600000, 10195183.000000],
    [731553.400000, 10216254.900000],
    [726133.700000, 10248243.800000],
    [723429.400000, 10269790.000000],
    [721464.000000, 10291484.300000],
    [719921.500000, 10324249.600000],
    [719842.400000, 10346205.300000],
    [720531.500000, 10368220.200000],
    [723019.500000, 10401295.000000],
    [725655.600000, 10423339.800000],
    [729078.800000, 10445347.700000],
    [735696.000000, 10478227.800000],
    [741098.600000, 10500017.500000],
    [744097.600000, 10510862.100000],
    [750690.500000, 10532432.200000],
    [754284.200000, 10543150.100000],
    [762064.700000, 10564432.500000],
    [766250.800000, 10574989.400000],
    [775212.000000, 10595915.200000],
    [779986.000000, 10606276.400000],
    [790116.800000, 10626775.900000],
    [795471.900000, 10636906.500000],
    [806756.500000, 10656909.600000],
    [812683.800000, 10666774.400000],
    [825102.000000, 10686211.000000],
    [831590.100000, 10695775.000000],
    [845116.800000, 10714575.100000],
    [852151.900000, 10723803.500000],
    [866757.000000, 10741897.900000],
    [874323.000000, 10750756.300000],
    [889971.600000, 10768076.700000],
    [906294.100000, 10784844.000000],
    [914702.000000, 10793011.100000],
    [931998.700000, 10808894.200000],
    [940882.100000, 10816603.100000],
    [959104.800000, 10831538.600000],
    [968438.400000, 10838758.400000],
    [987534.200000, 10852685.600000],
    [1007180.400000, 10865908.300000],
    [1017201.400000, 10872247.700000],
    [1037621.900000, 10884367.900000],
    [1048014.200000, 10890142.900000],
    [1069143.900000, 10901109.000000],
    [1079873.700000, 10906294.900000],
    [1107748.700000, 10918591.000000],
    [1110769.000000, 10946822.100000],
    [1114267.100000, 10970608.600000],
    [1116336.700000, 10982482.600000],
    [1121119.400000, 11006172.600000],
    [1123833.200000, 11017980.600000],
    [1129906.900000, 11041502.500000],
    [1133267.000000, 11053208.300000],
    [1140633.900000, 11076489.100000],
    [1144640.400000, 11088055.700000],
    [1153298.400000, 11111020.600000],
    [1157949.100000, 11122410.400000],
    [1167891.500000, 11144983.600000],
    [1173181.900000, 11156158.400000],
    [1184397.000000, 11178262.900000],
    [1196451.900000, 11200012.200000],
    [1202791.400000, 11210742.700000],
    [1216088.500000, 11231894.200000],
    [1223043.200000, 11242306.500000],
    [1237557.100000, 11262782.700000],
    [1245112.800000, 11272838.000000],
    [1260812.600000, 11292561.800000],
    [1277283.000000, 11311741.600000],
    [1285801.600000, 11321117.000000],
    [1303393.900000, 11339418.200000],
    [1321708.700000, 11357092.600000],
    [1331130.300000, 11365684.800000],
    [1350487.200000, 11382359.300000],
    [1370508.200000, 11398328.300000],
    [1380759.700000, 11406038.900000],
    [1401727.200000, 11420893.800000],
    [1423290.300000, 11434970.100000],
    [1434285.700000, 11441707.800000],
    [1456684.800000, 11454565.600000],
    [1479601.000000, 11466579.100000],
    [1491242.500000, 11472261.700000],
    [1514870.500000, 11482964.300000],
    [1538928.300000, 11492765.700000],
    [1563379.100000, 11501645.800000],
    [1588184.600000, 11509586.500000],
    [1600708.100000, 11513199.400000],
    [1625971.800000, 11519700.800000],
    [1651490.500000, 11525225.200000],
    [1664332.500000, 11527617.400000],
    [1690156.000000, 11531655.500000],
    [1716129.900000, 11534691.500000],
    [1729160.000000, 11535831.700000],
    [1755279.600000, 11537353.300000],
    [1781442.500000, 11537860.900000],
    [1813746.600000, 11537055.300000],
    [1821179.100000, 11558298.100000],
    [1830725.400000, 11582816.800000],
    [1841194.300000, 11607059.600000],
    [1846773.300000, 11619065.800000],
    [1858616.900000, 11642824.900000],
    [1871368.700000, 11666214.700000],
    [1885019.900000, 11689197.700000],
    [1892179.600000, 11700524.800000],
    [1907160.000000, 11722826.600000],
    [1923010.300000, 11744627.200000],
    [1939715.900000, 11765889.000000],
    [1948384.100000, 11776306.200000],
    [1966340.200000, 11796690.100000],
    [1975623.100000, 11806647.600000],
    [1994785.600000, 11826070.900000],
    [2004659.600000, 11835527.800000],
    [2024977.900000, 11853910.000000],
    [2046035.500000, 11871554.300000],
    [2067805.200000, 11888427.100000],
    [2078948.000000, 11896563.800000],
    [2101730.800000, 11912217.900000],
    [2113362.800000, 11919727.400000],
    [2137087.200000, 11934092.800000],
    [2149171.100000, 11940941.400000],
    [2173759.800000, 11953952.600000],
    [2198879.100000, 11966027.400000],
    [2224490.500000, 11977140.400000],
    [2237468.500000, 11982329.000000],
    [2263743.100000, 11991956.300000],
    [2277029.400000, 11996390.000000],
    [2303873.300000, 12004485.600000],
    [2317420.000000, 12008143.100000],
    [2344735.100000, 12014667.700000],
    [2358492.300000, 12017531.200000],
    [2386177.500000, 12022453.000000],
    [2400094.000000, 12024508.500000],
    [2428045.400000, 12027803.300000],
    [2442068.700000, 12029040.700000],
    [2470180.600000, 12030692.200000],
    [2498340.200000, 12031243.100000],
    [2512423.000000, 12031105.300000],
    [2540564.700000, 12030003.800000],
    [2568635.000000, 12027803.300000],
    [2582628.500000, 12026292.300000],
    [2610502.900000, 12022453.000000],
    [2638188.100000, 12017531.200000],
    [2651945.300000, 12014667.700000],
    [2679260.500000, 12008143.100000],
    [2692807.100000, 12004485.600000],
    [2719651.000000, 11996390.000000],
    [2732937.300000, 11991956.300000],
    [2759212.000000, 11982329.000000],
    [2785054.700000, 11971705.600000],
    [2797801.400000, 11966027.400000],
    [2822920.600000, 11953952.600000],
    [2835283.700000, 11947562.400000],
    [2859593.200000, 11934092.800000],
    [2871530.800000, 11927020.300000],
    [2894665.600000, 11912401.200000],
    [2907423.600000, 11919002.400000],
    [2932415.100000, 11931007.500000],
    [2957895.400000, 11942056.300000],
    [2970806.600000, 11947214.700000],
    [2996945.700000, 11956786.100000],
    [3010163.100000, 11961193.900000],
    [3036867.400000, 11969242.200000],
    [3050343.400000, 11972878.300000],
    [3077515.700000, 11979364.700000],
    [3091200.900000, 11982211.400000],
    [3118740.600000, 11987104.300000],
    [3132583.900000, 11989147.700000],
    [3160388.000000, 11992423.100000],
    [3174337.300000, 11993653.200000],
    [3202300.800000, 11995295.000000],
    [3230311.700000, 11995842.600000],
    [3244320.100000, 11995705.700000],
    [3272313.300000, 11994610.700000],
    [3286286.200000, 11993653.200000],
    [3314155.200000, 11990921.000000],
    [3328039.600000, 11989147.700000],
    [3355679.100000, 11984791.800000],
    [3369422.600000, 11982211.400000],
    [3396728.700000, 11976253.100000],
    [3410280.000000, 11972878.300000],
    [3437151.300000, 11965346.700000],
    [3450460.400000, 11961193.900000],
    [3476798.400000, 11952125.500000],
    [3489816.800000, 11947214.700000],
    [3515526.800000, 11936652.900000],
    [3528208.400000, 11931007.500000],
    [3553199.800000, 11919002.400000],
    [3577664.400000, 11906065.800000],
    [3601565.300000, 11892224.300000],
    [3613293.500000, 11884972.900000],
    [3636284.000000, 11869827.300000],
    [3658626.700000, 11853849.700000],
    [3680290.700000, 11837071.700000],
    [3690859.200000, 11828392.700000],
    [3711451.700000, 11810475.500000],
    [3731296.800000, 11791841.100000],
    [3750370.200000, 11772524.600000],
    [3759610.400000, 11762621.700000],
    [3777484.900000, 11742348.800000],
    [3794535.200000, 11721483.900000],
    [3810743.600000, 11700063.700000],
    [3818527.100000, 11689157.000000],
    [3833443.600000, 11666973.700000],
    [3847482.100000, 11644328.500000],
    [3860631.300000, 11621258.900000],
    [3866869.300000, 11609576.600000],
    [3878667.100000, 11585940.500000],
    [3889554.800000, 11561973.400000],
    [3899527.100000, 11537712.300000],
    [3904168.800000, 11525483.000000],
    [3912761.400000, 11500849.600000],
    [3920432.000000, 11476013.200000],
    [3927180.600000, 11451009.400000],
    [3933008.400000, 11425873.300000],
    [3937918.200000, 11400639.400000],
    [3941913.900000, 11375341.600000],
    [3945000.600000, 11350013.000000],
    [3947184.600000, 11324686.200000],
    [3947940.300000, 11312033.500000],
    [3948784.500000, 11286768.800000],
    [3948875.000000, 11274164.500000],
    [3948399.300000, 11249030.900000],
    [3947835.500000, 11236508.900000],
    [3946063.100000, 11211572.600000],
    [3943440.400000, 11186803.400000],
    [3941814.000000, 11174490.000000],
    [3937939.400000, 11150022.600000],
    [3935694.400000, 11137875.100000],
    [3930597.900000, 11113768.600000],
    [3927750.000000, 11101815.700000],
    [3921463.900000, 11078126.200000],
    [3914403.700000, 11054744.500000],
    [3906585.100000, 11031693.600000],
    [3898024.300000, 11008995.600000],
    [3888737.900000, 10986671.900000],
    [3878742.900000, 10964742.900000],
    [3868056.800000, 10943228.400000],
    [3856697.300000, 10922147.400000],
    [3844682.400000, 10901518.100000],
    [3832030.500000, 10881358.000000],
    [3818760.000000, 10861683.900000],
    [3804889.900000, 10842511.800000],
    [3790439.000000, 10823856.900000],
    [3775426.500000, 10805734.000000],
    [3759871.800000, 10788157.100000],
    [3743794.200000, 10771139.400000],
    [3720405.300000, 10748261.900000],
    [3723121.700000, 10734358.100000],
    [3727066.400000, 10710489.300000],
    [3728717.100000, 10698537.400000],
    [3731377.900000, 10674618.200000],
    [3732389.500000, 10662658.500000],
    [3733779.000000, 10638757.300000],
    [3734158.700000, 10626823.300000],
    [3734292.700000, 10603006.600000],
    [3734049.300000, 10591131.000000],
    [3732947.000000, 10567462.700000],
    [3732090.700000, 10555676.900000],
    [3729774.100000, 10532218.600000],
    [3728316.500000, 10520552.800000],
    [3724810.200000, 10497363.800000],
    [3718095.200000, 10462983.900000],
    [3712666.900000, 10440368.400000],
    [3703131.000000, 10406960.300000],
    [3695865.500000, 10385064.200000],
    [3683642.000000, 10352838.000000],
    [3674631.900000, 10331795.600000],
    [3659865.200000, 10300944.000000],
    [3649210.100000, 10280878.300000],
    [3635932.100000, 10257955.700000],
    [3636706.200000, 10233360.300000],
    [3636224.800000, 10199643.500000],
    [3634933.400000, 10177289.800000],
    [3631563.200000, 10144010.200000],
    [3628375.500000, 10122027.500000],
    [3622209.500000, 10089419.500000],
    [3614417.200000, 10057320.600000],
    [3605040.700000, 10025805.900000],
    [3597931.700000, 10005156.500000],
    [3590152.400000, 9984819.200000],
    [3581717.200000, 9964813.800000],
    [3567866.800000, 9935469.700000],
    [3557857.200000, 9916376.300000],
    [3541718.000000, 9888483.600000],
    [3530232.000000, 9870411.100000],
    [3514151.600000, 9847176.100000],
    [3535796.000000, 9836686.600000],
    [3563741.900000, 9821364.200000],
    [3590721.200000, 9804531.900000],
    [3616650.200000, 9786257.400000],
    [3633313.900000, 9773308.700000],
    [3657321.600000, 9752793.600000],
    [3680083.600000, 9731038.600000],
    [3701537.400000, 9708126.500000],
    [3715085.200000, 9692251.000000],
    [3734236.800000, 9667601.300000],
    [3751944.100000, 9642027.100000],
    [3768167.500000, 9615618.800000],
    [3782873.500000, 9588467.800000],
    [3796034.600000, 9560666.000000],
    [3807629.500000, 9532305.500000],
    [3817642.200000, 9503477.800000],
    [3823433.200000, 9484044.900000],
    [3830789.200000, 9454640.500000],
    [3834805.500000, 9434906.100000],
    [3839500.700000, 9405172.500000],
    [3841747.500000, 9385299.300000],
    [3843800.400000, 9355476.500000],
    [3844285.500000, 9325711.600000],
    [3843221.100000, 9296085.700000],
    [3841661.400000, 9276451.100000],
    [3838065.200000, 9247230.000000],
    [3832986.200000, 9218351.700000],
    [3826455.500000, 9189887.800000],
    [3821311.800000, 9171176.200000],
    [3812438.500000, 9143554.600000],
    [3802210.200000, 9116524.800000],
    [3791843.100000, 9092715.500000],
    [3796583.300000, 9082906.100000],
    [3804396.100000, 9065457.600000],
    [3811560.500000, 9047782.500000],
    [3818071.800000, 9029905.900000],
    [3823926.400000, 9011852.800000],
    [3829121.700000, 8993648.100000],
    [3833655.900000, 8975316.300000],
    [3837528.300000, 8956882.000000],
    [3842096.500000, 8929091.400000],
    [3844317.000000, 8910506.500000],
    [3845880.100000, 8891902.700000],
    [3846788.900000, 8873303.300000],
    [3847047.300000, 8854731.100000],
    [3846660.000000, 8836208.600000],
    [3845632.400000, 8817757.800000],
    [3843970.500000, 8799400.300000],
    [3841681.300000, 8781157.400000],
    [3838772.000000, 8763049.700000],
    [3833263.200000, 8736186.100000],
    [3828840.200000, 8718504.200000],
    [3821103.500000, 8692370.000000],
    [3815225.500000, 8675233.100000],
    [3805354.900000, 8650001.100000],
    [3798088.600000, 8633520.400000],
    [3790287.000000, 8617331.600000],
    [3781962.300000, 8601450.700000],
    [3768522.000000, 8578240.300000],
    [3758945.200000, 8563195.700000],
    [3743688.200000, 8541307.800000],
    [3732942.300000, 8527188.300000],
    [3715997.000000, 8506750.000000],
    [3704169.900000, 8493636.300000],
    [3691935.800000, 8480945.800000],
    [3679309.500000, 8468689.700000],
    [3659667.700000, 8451143.000000],
    [3646127.200000, 8440018.200000],
    [3632248.000000, 8429362.900000],
    [3618045.800000, 8419186.100000],
    [3596171.400000, 8404836.200000],
    [3581231.100000, 8395891.500000],
    [3566023.500000, 8387452.700000],
    [3550565.100000, 8379526.800000],
    [3526942.600000, 8368613.400000],
    [3510928.400000, 8361996.500000],
    [3486549.500000, 8353072.300000],
    [3470082.800000, 8347797.000000],
    [3448110.100000, 8341682.800000],
    [3441442.000000, 8331611.000000],
    [3431494.600000, 8317520.900000],
    [3415741.300000, 8297078.500000],
    [3399030.500000, 8277505.200000],
    [3381409.400000, 8258841.100000],
    [3362926.500000, 8241123.700000],
    [3343631.400000, 8224388.100000],
    [3323574.300000, 8208667.100000],
    [3304936.400000, 8195443.700000],
    [3310073.500000, 8171018.600000],
    [3312821.500000, 8154364.300000],
    [3314977.900000, 8137660.700000],
    [3316543.800000, 8120928.900000],
    [3317521.300000, 8104189.700000],
    [3317890.400000, 8079112.300000],
    [3317410.300000, 8062444.400000],
    [3315612.000000, 8037567.900000],
    [3313702.000000, 8021095.300000],
    [3309785.300000, 7996601.800000],
    [3306482.700000, 7980443.200000],
    [3300510.100000, 7956506.100000],
    [3295861.000000, 7940774.100000],
    [3287907.900000, 7917557.500000],
    [3281966.300000, 7902358.100000],
    [3272119.100000, 7880016.300000],
    [3264946.000000, 7865448.900000],
    [3253300.900000, 7844125.700000],
    [3244963.600000, 7830282.800000],
    [3231625.900000, 7810111.500000],
    [3222197.200000, 7797078.500000],
    [3207279.600000, 7778181.800000],
    [3196837.000000, 7766036.700000],
    [3180459.300000, 7748526.600000],
    [3169084.400000, 7737340.500000],
    [3151371.900000, 7721318.100000],
    [3132920.600000, 7706232.300000],
    [3113778.700000, 7692113.600000],
    [3100657.700000, 7683252.300000],
    [3080473.100000, 7670806.300000],
    [3059729.400000, 7659397.100000],
    [3045051.900000, 7652115.200000],
    [3059167.700000, 7641556.400000],
    [3071353.100000, 7631667.700000],
    [3083155.200000, 7621354.200000],
    [3100107.900000, 7605122.400000],
    [3110889.200000, 7593817.800000],
    [3126248.400000, 7576178.500000],
    [3135928.700000, 7563989.900000],
    [3149583.000000, 7545108.400000],
    [3158094.000000, 7532148.600000],
    [3166121.100000, 7518913.000000],
    [3173656.000000, 7505420.500000],
    [3184019.900000, 7484742.100000],
    [3190294.200000, 7470691.500000],
    [3198739.800000, 7449266.700000],
    [3203719.900000, 7434779.300000],
    [3210205.800000, 7412790.600000],
    [3213870.000000, 7397988.100000],
    [3217004.400000, 7383093.400000],
    [3219608.100000, 7368125.900000],
    [3222518.300000, 7345579.800000],
    [3223795.900000, 7330513.400000],
    [3224722.100000, 7307907.200000],
    [3224682.800000, 7292858.800000],
    [3223646.400000, 7270365.400000],
    [3222309.300000, 7255448.400000],
    [3219345.900000, 7233234.800000],
    [3216739.400000, 7218558.500000],
    [3211897.700000, 7196785.100000],
    [3208058.300000, 7182454.000000],
    [3201399.300000, 7161273.600000],
    [3196371.300000, 7147386.800000],
    [3187966.900000, 7126944.200000],
    [3181801.700000, 7113595.300000],
    [3175197.400000, 7100486.400000],
    [3168163.700000, 7087631.200000],
    [3156829.600000, 7068853.000000],
    [3148766.400000, 7056689.000000],
    [3135937.900000, 7039006.100000],
    [3126912.600000, 7027609.800000],
    [3112694.000000, 7011132.000000],
    [3102778.500000, 7000573.400000],
    [3092527.800000, 6990368.100000],
    [3081954.400000, 6980525.700000],
    [3065516.900000, 6966463.000000],
    [3054192.200000, 6957568.000000],
    [3036688.400000, 6944966.200000],
    [3024694.100000, 6937070.300000],
    [3006248.700000, 6926002.200000],
    [2993669.100000, 6919150.600000],
    [2980879.500000, 6912727.900000],
    [2967893.800000, 6906739.800000],
    [2948078.500000, 6898584.500000],
    [2934664.600000, 6893705.700000],
    [2914273.800000, 6887235.600000],
    [2900521.000000, 6883493.200000],
    [2879689.900000, 6878744.000000],
    [2865689.000000, 6876158.400000],
    [2844554.200000, 6873156.200000],
    [2830396.700000, 6871741.500000],
    [2809096.000000, 6870502.600000],
    [2787762.000000, 6870325.600000],
    [2773545.500000, 6870797.700000],
    [2759351.200000, 6871741.500000],
    [2742327.200000, 6873514.900000],
    [2741529.600000, 6856198.800000],
    [2740314.800000, 6841988.700000],
    [2738611.900000, 6827854.000000],
    [2736425.700000, 6813811.200000],
    [2733761.000000, 6799876.600000],
    [2730623.600000, 6786065.900000],
    [2727019.500000, 6772394.900000],
    [2722402.700000, 6757199.000000],
    [2732817.400000, 6746812.300000],
    [2742395.200000, 6736520.800000],
    [2751585.200000, 6725903.600000],
    [2764619.700000, 6709400.900000],
    [2772794.700000, 6698037.800000],
    [2780547.200000, 6686404.400000],
    [2787868.900000, 6674517.200000],
    [2798027.000000, 6656247.200000],
    [2804239.500000, 6643798.900000],
    [2809997.200000, 6631156.000000],
    [2815294.800000, 6618335.600000],
    [2822368.700000, 6598810.200000],
    [2826497.400000, 6585621.900000],
    [2830152.700000, 6572316.700000],
    [2833332.400000, 6558912.100000],
    [2837206.600000, 6538656.500000],
    [2840004.000000, 6518274.100000],
    [2841724.700000, 6497822.500000],
    [2842371.900000, 6477358.700000],
    [2842209.900000, 6463737.200000],
    [2841576.000000, 6450151.600000],
    [2839747.100000, 6429876.200000],
    [2837948.000000, 6416451.100000],
    [2835689.900000, 6403117.700000],
    [2831453.000000, 6383323.200000],
    [2828069.900000, 6370285.800000],
    [2824246.900000, 6357392.900000],
    [2819990.500000, 6344658.700000],
    [2815307.600000, 6332097.400000],
    [2810205.500000, 6319722.800000],
    [2801783.300000, 6301540.000000],
    [2795667.600000, 6289690.000000],
    [2789161.400000, 6278072.300000],
    [2782273.400000, 6266699.000000],
    [2771246.300000, 6250123.400000],
    [2763445.600000, 6239412.600000],
    [2755297.100000, 6228986.300000],
    [2746811.200000, 6218855.100000],
    [2733473.000000, 6204234.100000],
    [2724190.600000, 6194884.800000],
    [2714609.100000, 6185864.900000],
    [2704739.800000, 6177183.200000],
    [2691157.500000, 6166167.000000],
    [2685236.600000, 6143135.200000],
    [2681501.000000, 6131131.300000],
    [2677361.500000, 6119279.900000],
    [2672824.600000, 6107594.100000],
    [2667897.100000, 6096086.800000],
    [2662586.200000, 6084770.600000],
    [2656899.600000, 6073657.700000],
    [2650845.300000, 6062760.200000],
    [2644431.400000, 6052089.700000],
    [2637666.600000, 6041657.500000],
    [2630559.800000, 6031474.600000],
    [2623120.300000, 6021551.700000],
    [2615357.600000, 6011899.000000],
    [2607281.300000, 6002526.500000],
    [2598901.600000, 5993443.900000],
    [2590228.800000, 5984660.300000],
    [2581273.200000, 5976184.700000],
    [2572045.800000, 5968025.600000],
    [2557718.900000, 5956398.100000],
    [2547860.100000, 5949065.100000],
    [2532639.200000, 5938711.700000],
    [2522220.500000, 5932250.200000],
    [2506214.900000, 5923234.500000],
    [2495310.400000, 5917683.700000],
    [2478632.900000, 5910060.400000],
    [2461607.700000, 5903295.000000],
    [2444277.100000, 5897402.200000],
    [2432574.800000, 5893964.800000],
    [2414830.400000, 5889553.300000],
    [2396895.500000, 5886043.800000],
    [2381803.300000, 5883822.500000],
    [2363518.300000, 5868675.200000],
    [2374877.300000, 5852818.000000],
    [2385069.100000, 5836819.200000],
    [2394377.600000, 5820327.400000],
    [2402780.900000, 5803393.000000],
    [2410259.900000, 5786067.400000],
    [2416798.700000, 5768402.600000],
    [2422384.400000, 5750450.700000],
    [2425573.600000, 5738349.200000],
    [2428332.400000, 5726159.000000],
    [2431660.100000, 5707741.600000],
    [2434012.500000, 5689211.900000],
    [2435389.100000, 5670622.100000],
    [2435765.800000, 5658220.900000],
    [2435711.100000, 5645831.000000],
    [2434824.300000, 5627300.100000],
    [2432979.500000, 5608878.300000],
    [2430187.300000, 5590614.500000],
    [2427806.000000, 5578550.100000],
    [2425014.000000, 5566591.400000],
    [2421816.100000, 5554751.900000],
    [2416270.100000, 5537245.800000],
    [2412081.900000, 5525762.900000],
    [2405079.000000, 5508852.700000],
    [2399940.000000, 5497806.600000],
    [2391544.100000, 5481609.200000],
    [2382346.500000, 5465892.700000],
    [2372375.100000, 5450695.200000],
    [2365312.000000, 5440870.000000],
    [2354116.600000, 5426617.800000],
    [2342228.800000, 5412978.100000],
    [2329681.600000, 5399982.700000],
    [2320967.400000, 5391692.100000],
    [2307398.200000, 5379836.900000],
    [2293263.900000, 5368702.500000],
    [2281250.200000, 5360124.100000],
    [2284373.200000, 5337427.100000],
    [2285396.200000, 5325503.200000],
    [2286002.400000, 5313565.800000],
    [2286192.700000, 5301629.600000],
    [2285968.500000, 5289708.900000],
    [2285331.500000, 5277818.300000],
    [2284284.100000, 5265971.900000],
    [2281949.900000, 5248316.100000],
    [2279889.800000, 5236641.500000],
    [2277431.100000, 5225059.700000],
    [2274578.100000, 5213584.100000],
    [2271335.800000, 5202227.700000],
    [2267709.100000, 5191003.700000],
    [2263703.900000, 5179924.700000],
    [2259326.000000, 5169003.200000],
    [2254581.800000, 5158251.400000],
    [2249478.100000, 5147681.200000],
    [2244022.000000, 5137304.300000],
    [2238220.800000, 5127132.100000],
    [2232082.400000, 5117175.600000],
    [2218826.400000, 5097952.800000],
    [2204322.200000, 5079718.300000],
    [2188642.600000, 5062549.400000],
    [2171864.600000, 5046518.200000],
    [2154068.900000, 5031691.400000],
    [2140104.800000, 5021398.500000],
    [2120732.600000, 5008822.700000],
    [2100581.600000, 4997605.400000],
    [2079743.700000, 4987791.700000],
    [2063721.000000, 4981376.500000],
    [2041908.600000, 4974110.200000],
    [2019672.400000, 4968341.400000],
    [1997110.900000, 4964092.700000],
    [1974323.500000, 4961381.000000],
    [1951410.500000, 4960217.000000],
    [1928472.200000, 4960605.100000],
    [1905609.500000, 4962544.000000],
    [1882922.500000, 4966025.800000],
    [1860511.300000, 4971037.100000],
    [1838474.800000, 4977557.900000],
    [1822252.200000, 4983423.800000],
    [1806336.200000, 4990111.300000],
    [1790767.100000, 4997605.400000],
    [1775584.300000, 5005889.200000],
    [1765696.500000, 5011841.200000],
    [1751243.900000, 5021398.500000],
    [1741877.900000, 5028180.100000],
    [1728259.900000, 5038950.300000],
    [1719484.100000, 5046518.200000],
    [1706800.700000, 5058432.300000],
    [1698680.100000, 5066737.700000],
    [1687026.500000, 5079718.300000],
    [1679622.800000, 5088707.100000],
    [1669088.600000, 5102669.000000],
    [1662459.500000, 5112281.700000],
    [1656155.400000, 5122126.200000],
    [1647767.000000, 5136516.700000],
    [1625016.100000, 5141597.300000],
    [1602585.700000, 5148223.800000],
    [1580634.300000, 5156358.500000],
    [1564543.900000, 5163430.300000],
    [1543664.800000, 5174123.700000],
    [1528488.300000, 5183068.700000],
    [1508970.700000, 5196190.300000],
    [1494920.300000, 5206898.800000],
    [1485853.800000, 5214437.400000],
    [1477040.300000, 5222286.700000],
    [1464317.100000, 5234625.400000],
    [1452221.100000, 5247616.400000],
    [1440785.500000, 5261229.200000],
    [1430042.500000, 5275431.700000],
    [1420022.600000, 5290190.000000],
    [1410755.000000, 5305468.600000],
    [1402355.000000, 5321060.700000],
    [1391096.900000, 5327527.100000],
    [1380966.700000, 5333839.800000],
    [1363880.800000, 5345635.300000],
    [1372234.800000, 5328215.600000],
    [1379268.000000, 5311227.500000],
    [1385383.100000, 5293920.500000],
    [1390567.800000, 5276345.500000],
    [1394812.900000, 5258553.500000],
    [1398111.600000, 5240595.400000],
    [1400460.300000, 5222522.200000],
    [1401857.800000, 5204384.600000],
    [1402305.800000, 5186232.800000],
    [1401808.500000, 5168116.600000],
    [1400372.500000, 5150084.900000],
    [1398007.000000, 5132186.200000],
    [1394723.600000, 5114467.800000],
    [1390536.100000, 5096976.100000],
    [1385460.400000, 5079756.500000],
    [1379514.700000, 5062853.000000],
    [1372719.200000, 5046308.700000],
    [1365095.900000, 5030164.900000],
    [1356668.800000, 5014461.900000],
    [1347463.600000, 4999238.300000],
    [1337507.600000, 4984531.300000],
    [1326829.800000, 4970376.400000],
    [1315460.500000, 4956807.500000],
    [1303431.600000, 4943857.000000],
    [1290776.200000, 4931555.200000],
    [1277528.600000, 4919931.200000],
    [1263724.400000, 4909011.800000],
    [1249400.000000, 4898822.200000],
    [1234593.000000, 4889386.000000],
    [1219341.700000, 4880724.500000],
    [1203685.400000, 4872857.600000],
    [1182249.300000, 4863634.600000],
    [1165804.200000, 4857687.200000],
    [1143465.400000, 4851075.800000],
    [1126456.400000, 4847120.500000],
    [1107003.400000, 4843705.800000],
    [1093334.900000, 4838008.800000],
    [1082431.800000, 4833960.000000],
    [1060260.900000, 4826986.200000],
    [1037685.800000, 4821533.200000],
    [1014806.500000, 4817622.900000],
    [1003284.400000, 4816251.200000],
    [985933.500000, 4814927.000000],
    [968539.700000, 4814485.300000],
    [951145.900000, 4814927.000000],
    [933795.000000, 4816251.200000],
    [916530.000000, 4818454.900000],
    [899393.600000, 4821533.200000],
    [888061.800000, 4824068.100000],
    [865676.300000, 4830284.500000],
    [849179.500000, 4835938.000000],
    [832979.300000, 4842427.000000],
    [817116.600000, 4849737.100000],
    [801631.700000, 4857851.800000],
    [786564.100000, 4866752.600000],
    [771952.700000, 4876419.400000],
    [757835.600000, 4886829.900000],
    [739845.500000, 4901825.900000],
    [727024.200000, 4913875.500000],
    [714816.700000, 4926582.000000],
    [703256.500000, 4939915.400000],
    [692375.800000, 4953844.100000],
    [682205.400000, 4968334.700000],
    [672774.700000, 4983352.400000],
    [662450.800000, 5002087.500000],
    [650604.400000, 5003899.100000],
    [633197.600000, 5007325.200000],
    [615978.700000, 5011631.300000],
    [604624.900000, 5014986.300000],
    [587813.700000, 5020737.100000],
    [576770.900000, 5025044.200000],
    [560484.200000, 5032204.200000],
    [546139.400000, 5039366.700000],
    [532689.300000, 5028375.700000],
    [523194.100000, 5021259.800000],
  ]),
);
// An island no radar reaches, about 2,792 km², at 57.68, 20.31.
radarCoverageInv.appendLinearRing(
  new LinearRing([
    [2204365.500000, 7841710.400000],
    [2217240.200000, 7851425.800000],
    [2230477.700000, 7860674.100000],
    [2250976.100000, 7873638.700000],
    [2272190.500000, 7885472.200000],
    [2294056.300000, 7896131.300000],
    [2316506.200000, 7905576.800000],
    [2339469.800000, 7913773.500000],
    [2362874.500000, 7920690.600000],
    [2378685.900000, 7924578.100000],
    [2390160.000000, 7926995.400000],
    [2384923.800000, 7942951.100000],
    [2384244.500000, 7943666.700000],
    [2360290.000000, 7945445.000000],
    [2344019.600000, 7947319.500000],
    [2319741.600000, 7951130.000000],
    [2303665.000000, 7954333.300000],
    [2287694.400000, 7958064.000000],
    [2261970.200000, 7965269.100000],
    [2241021.400000, 7948589.900000],
    [2222348.000000, 7935283.200000],
    [2220009.100000, 7921214.800000],
    [2216919.400000, 7905838.700000],
    [2211313.400000, 7883054.500000],
    [2204564.300000, 7860656.300000],
    [2199443.300000, 7845965.800000],
    [2194833.100000, 7833999.500000],
    [2204365.500000, 7841710.400000],
  ]),
);
// An island no radar reaches, about 872 km², at 44.23, 10.98.
radarCoverageInv.appendLinearRing(
  new LinearRing([
    [1231122.300000, 5540396.300000],
    [1222205.300000, 5531910.800000],
    [1213221.600000, 5523933.900000],
    [1203981.700000, 5516273.500000],
    [1190828.500000, 5506246.500000],
    [1207994.100000, 5498447.800000],
    [1224565.100000, 5489799.700000],
    [1240629.900000, 5480274.400000],
    [1259343.000000, 5467592.000000],
    [1249814.600000, 5487337.300000],
    [1242831.000000, 5504209.400000],
    [1236712.600000, 5521452.300000],
    [1231122.300000, 5540396.300000],
  ]),
);
// An island no radar reaches, about 381 km², at 47.30, 13.95.
radarCoverageInv.appendLinearRing(
  new LinearRing([
    [1586568.900000, 5997441.600000],
    [1571553.600000, 5995886.400000],
    [1551548.100000, 5994714.300000],
    [1538190.500000, 5994490.900000],
    [1519829.500000, 5994923.500000],
    [1513733.700000, 5987856.900000],
    [1505902.400000, 5979597.500000],
    [1527335.000000, 5983037.100000],
    [1546467.900000, 5984962.200000],
    [1559270.800000, 5985663.300000],
    [1572091.400000, 5985897.100000],
    [1592840.100000, 5985273.700000],
    [1586568.900000, 5997441.600000],
  ]),
);
// An island no radar reaches, about 94 km², at 66.16, 16.75.
radarCoverageInv.appendLinearRing(
  new LinearRing([
    [1873713.700000, 9881592.600000],
    [1881626.500000, 9911130.500000],
    [1861335.100000, 9929506.800000],
    [1848145.100000, 9942389.700000],
    [1854032.500000, 9930043.600000],
    [1862343.100000, 9911105.300000],
    [1869946.300000, 9891931.800000],
    [1873713.700000, 9881592.600000],
  ]),
);
// A hole: Ireland, whose radars reach no other network's.
radarCoverageInv.appendLinearRing(
  new LinearRing([
    [-718856.100000, 6631892.500000],
    [-729226.400000, 6622821.900000],
    [-739883.300000, 6614115.100000],
    [-750814.200000, 6605780.300000],
    [-762006.700000, 6597825.500000],
    [-773447.800000, 6590258.300000],
    [-785124.700000, 6583085.600000],
    [-797024.300000, 6576314.100000],
    [-815262.000000, 6566922.900000],
    [-827659.900000, 6561181.100000],
    [-840233.500000, 6555860.800000],
    [-852969.100000, 6550966.800000],
    [-872346.300000, 6544434.800000],
    [-885426.600000, 6540625.100000],
    [-898620.400000, 6537255.600000],
    [-911913.700000, 6534329.200000],
    [-932009.200000, 6530776.100000],
    [-945489.300000, 6528968.200000],
    [-959019.300000, 6527610.800000],
    [-972585.000000, 6526705.300000],
    [-992969.200000, 6526195.600000],
    [-1013353.400000, 6526705.300000],
    [-1026919.000000, 6527610.800000],
    [-1040449.000000, 6528968.200000],
    [-1053929.100000, 6530776.100000],
    [-1074024.700000, 6534329.200000],
    [-1087317.900000, 6537255.600000],
    [-1100511.800000, 6540625.100000],
    [-1113592.100000, 6544434.800000],
    [-1132969.300000, 6550966.800000],
    [-1145704.900000, 6555860.800000],
    [-1158278.500000, 6561181.100000],
    [-1170676.400000, 6566922.900000],
    [-1188914.100000, 6576314.100000],
    [-1200813.600000, 6583085.600000],
    [-1212490.500000, 6590258.300000],
    [-1223931.700000, 6597825.500000],
    [-1240623.100000, 6609900.700000],
    [-1251418.600000, 6618422.500000],
    [-1261933.700000, 6627312.200000],
    [-1272156.200000, 6636561.500000],
    [-1286914.800000, 6651089.800000],
    [-1296353.000000, 6661197.800000],
    [-1305457.100000, 6671632.400000],
    [-1314215.800000, 6682383.200000],
    [-1322618.000000, 6693439.500000],
    [-1330652.900000, 6704790.300000],
    [-1338310.000000, 6716423.900000],
    [-1345579.100000, 6728328.600000],
    [-1355733.900000, 6746666.900000],
    [-1361990.600000, 6759194.700000],
    [-1367826.300000, 6771949.000000],
    [-1373232.400000, 6784916.200000],
    [-1378200.400000, 6798082.400000],
    [-1382722.600000, 6811433.300000],
    [-1386791.600000, 6824954.300000],
    [-1390400.300000, 6838630.400000],
    [-1394936.100000, 6859402.000000],
    [-1397366.500000, 6873398.500000],
    [-1399315.700000, 6887495.300000],
    [-1400778.900000, 6901676.100000],
    [-1401751.800000, 6915924.300000],
    [-1402230.500000, 6930223.000000],
    [-1402212.000000, 6944555.100000],
    [-1401693.500000, 6958903.300000],
    [-1400673.000000, 6973250.100000],
    [-1399149.000000, 6987577.700000],
    [-1395917.400000, 7008994.100000],
    [-1391551.100000, 7030266.100000],
    [-1386053.600000, 7051332.300000],
    [-1379431.300000, 7072130.700000],
    [-1371694.600000, 7092599.300000],
    [-1362857.200000, 7112676.000000],
    [-1352936.400000, 7132299.100000],
    [-1345731.000000, 7145098.800000],
    [-1338060.600000, 7157651.600000],
    [-1325700.700000, 7175979.000000],
    [-1312342.300000, 7193651.700000],
    [-1298019.000000, 7210611.900000],
    [-1282768.000000, 7226803.300000],
    [-1266630.100000, 7242171.500000],
    [-1249649.800000, 7256664.300000],
    [-1231874.800000, 7270232.000000],
    [-1219608.400000, 7278739.800000],
    [-1207027.400000, 7286802.100000],
    [-1194148.300000, 7294406.400000],
    [-1180988.200000, 7301540.700000],
    [-1167564.800000, 7308193.600000],
    [-1153896.300000, 7314354.600000],
    [-1140001.200000, 7320013.900000],
    [-1125898.700000, 7325162.200000],
    [-1111608.300000, 7329791.400000],
    [-1097149.800000, 7333893.900000],
    [-1082543.600000, 7337463.100000],
    [-1067810.100000, 7340493.100000],
    [-1052970.300000, 7342979.000000],
    [-1038045.000000, 7344916.700000],
    [-1013617.600000, 7346877.600000],
    [-997360.000000, 7364901.500000],
    [-981142.400000, 7380954.600000],
    [-969837.700000, 7391172.000000],
    [-958154.900000, 7400983.300000],
    [-939954.000000, 7414906.100000],
    [-927391.500000, 7423637.400000],
    [-914505.300000, 7431912.200000],
    [-894606.100000, 7443440.000000],
    [-874076.300000, 7453870.300000],
    [-852978.600000, 7463165.400000],
    [-831378.000000, 7471291.500000],
    [-809341.900000, 7478218.900000],
    [-786939.800000, 7483922.100000],
    [-764242.800000, 7488380.100000],
    [-741323.400000, 7491576.300000],
    [-718255.200000, 7493498.800000],
    [-695112.300000, 7494140.400000],
    [-671969.400000, 7493498.800000],
    [-656577.700000, 7492359.100000],
    [-633600.400000, 7489586.400000],
    [-618387.800000, 7487033.600000],
    [-595781.100000, 7482158.500000],
    [-580882.700000, 7478218.900000],
    [-558846.600000, 7471291.500000],
    [-537246.000000, 7463165.400000],
    [-516148.300000, 7453870.300000],
    [-502394.800000, 7447040.700000],
    [-482278.600000, 7435874.300000],
    [-462833.100000, 7423637.400000],
    [-444116.000000, 7410373.500000],
    [-432069.700000, 7400983.300000],
    [-414686.400000, 7386113.100000],
    [-398169.700000, 7370346.800000],
    [-382564.900000, 7353738.400000],
    [-372689.100000, 7342225.900000],
    [-358695.000000, 7324339.600000],
    [-345715.800000, 7305764.900000],
    [-333783.000000, 7286562.400000],
    [-326422.900000, 7273442.400000],
    [-316293.700000, 7253331.000000],
    [-307276.200000, 7232758.300000],
    [-299387.900000, 7211787.700000],
    [-294763.300000, 7197617.800000],
    [-288785.600000, 7176125.700000],
    [-283965.600000, 7154405.600000],
    [-280307.200000, 7132520.700000],
    [-278514.100000, 7117870.200000],
    [-276791.600000, 7095849.500000],
    [-276225.100000, 7073829.500000],
    [-276806.600000, 7051870.400000],
    [-277826.600000, 7037294.100000],
    [-280295.400000, 7015567.400000],
    [-283876.500000, 6994056.500000],
    [-288551.300000, 6972816.900000],
    [-292265.300000, 6958834.500000],
    [-298715.400000, 6938166.000000],
    [-306198.200000, 6917908.800000],
    [-314686.700000, 6898112.200000],
    [-320890.100000, 6885193.900000],
    [-330989.100000, 6866269.600000],
    [-342011.900000, 6847928.200000],
    [-353924.700000, 6830212.100000],
    [-362343.400000, 6818768.800000],
    [-375659.600000, 6802183.600000],
    [-389767.700000, 6786326.800000],
    [-404628.300000, 6771233.200000],
    [-414933.500000, 6761611.200000],
    [-430957.700000, 6747861.900000],
    [-442000.000000, 6739164.000000],
    [-459070.400000, 6726840.500000],
    [-470769.700000, 6719118.100000],
    [-488764.400000, 6708292.300000],
    [-507253.600000, 6698395.800000],
    [-526191.400000, 6689448.900000],
    [-545531.400000, 6681469.900000],
    [-565226.400000, 6674475.000000],
    [-585228.900000, 6668478.300000],
    [-598711.200000, 6665041.100000],
    [-612294.600000, 6662056.000000],
    [-632827.800000, 6658431.800000],
    [-653507.800000, 6655837.900000],
    [-674285.500000, 6654279.500000],
    [-696477.400000, 6653771.000000],
    [-708784.500000, 6641318.100000],
    [-718856.100000, 6631892.500000],
  ]),
);

/**
 * Everywhere DWD's radars do *not* reach: the world, with DWD's outline cut out.
 *
 * What the wash covers on a forecast step. The forecast is DWD's WN alone,
 * which no other network forecasts beside, so outside this hole the map has
 * nothing to show for that time, whatever it shows for the present.
 *
 * The same outline `radarCoverageInv` draws DWD's part of, generated the same
 * way: 150 km circles around DWD's seventeen sites, merged with shapely in
 * EPSG:3857 and simplified to 200 m. It lies inside that merged hole, to the
 * square kilometre.
 */
export const dwdCoverageInv = fromExtent(
  transformExtent([-180, -90, 180, 90], "EPSG:4326", "EPSG:3857"),
);
dwdCoverageInv.appendLinearRing(
  new LinearRing([
    [1034565.268699, 5917294.241297],
    [1048954.592373, 5910450.909607],
    [1063955.915145, 5904595.711387],
    [1074167.768588, 5901301.147478],
    [1084521.567900, 5898501.829949],
    [1094994.032167, 5896203.554604],
    [1110873.802463, 5893705.699648],
    [1121539.827183, 5892678.512875],
    [1137598.589343, 5892100.099755],
    [1153657.351504, 5892678.512875],
    [1169635.503847, 5894411.071664],
    [1185452.748199, 5897289.740545],
    [1201029.410099, 5901301.147478],
    [1216286.751688, 5906426.615126],
    [1231147.285771, 5912642.205112],
    [1245535.091335, 5919918.775937],
    [1254827.685819, 5925342.540980],
    [1262478.474151, 5930292.226864],
    [1273339.846905, 5926170.209961],
    [1288750.308825, 5921402.601947],
    [1299192.053492, 5918847.210183],
    [1315048.237618, 5915961.846766],
    [1325713.565667, 5914675.874999],
    [1341793.814397, 5913709.946183],
    [1357901.004755, 5913903.231401],
    [1373954.323401, 5915254.835666],
    [1384586.783932, 5916796.544046],
    [1395135.698453, 5918847.210183],
    [1410750.793776, 5922867.960174],
    [1426045.894080, 5928005.389859],
    [1440943.344244, 5934235.522515],
    [1450616.412426, 5938981.799641],
    [1460056.798862, 5944190.934351],
    [1473733.802322, 5952848.323013],
    [1486767.407302, 5962481.282283],
    [1499089.050200, 5973043.857692],
    [1510633.121483, 5984485.311532],
    [1521337.289043, 5996750.269538],
    [1531142.821494, 6009778.888270],
    [1539994.910401, 6023507.044632],
    [1547842.990078, 6037866.548988],
    [1554641.053343, 6052785.383313],
    [1560347.961236, 6068187.965743],
    [1564927.744431, 6083995.442740],
    [1567339.453393, 6094718.416012],
    [1569228.906064, 6105560.142788],
    [1571070.037334, 6121989.805276],
    [1571705.100542, 6138542.229887],
    [1571123.680164, 6155128.475095],
    [1569321.873893, 6171658.383166],
    [1566302.467221, 6188041.058751],
    [1562075.073692, 6204185.374456],
    [1556491.122776, 6220414.485474],
    [1566334.927831, 6232863.358844],
    [1572735.896782, 6242020.210010],
    [1581503.474446, 6256329.960222],
    [1586770.732515, 6266222.096817],
    [1593770.334943, 6281533.761431],
    [1597817.489227, 6292023.773248],
    [1601355.886346, 6302711.956805],
    [1604375.285655, 6313573.926369],
    [1607911.625586, 6330137.954258],
    [1609596.164484, 6341323.970127],
    [1610734.921705, 6352594.120982],
    [1611322.981692, 6363921.772534],
    [1611164.912131, 6380961.949861],
    [1610363.177726, 6392314.666796],
    [1609003.788244, 6403629.292636],
    [1606178.321833, 6419238.989722],
    [1621611.107233, 6424837.290111],
    [1637246.002116, 6431797.828433],
    [1652356.518800, 6439871.903213],
    [1662100.504351, 6445854.972337],
    [1676165.946377, 6455699.109917],
    [1689505.669090, 6466545.276050],
    [1702049.134993, 6478342.234172],
    [1709935.699360, 6486707.544673],
    [1721004.247482, 6499961.927320],
    [1731105.153437, 6514006.704329],
    [1740181.387299, 6528773.299286],
    [1745636.802380, 6538982.897013],
    [1752890.837695, 6554788.617442],
    [1757088.307665, 6565618.984818],
    [1762397.136805, 6582243.301555],
    [1765262.891211, 6593541.867765],
    [1767579.221973, 6604982.733657],
    [1769338.351478, 6616539.349953],
    [1770533.746487, 6628184.673522],
    [1771160.154690, 6639891.222573],
    [1771213.639263, 6651631.134892],
    [1770691.611146, 6663376.229108],
    [1769592.858773, 6675098.068949],
    [1767917.574967, 6686768.030431],
    [1765667.380699, 6698357.371876],
    [1762845.345418, 6709837.306661],
    [1759456.003667, 6721179.078522],
    [1755505.367660, 6732354.039250],
    [1751000.935565, 6743333.728553],
    [1745951.695192, 6754089.955839],
    [1737272.866826, 6769918.403943],
    [1747720.389921, 6784403.437763],
    [1757121.797110, 6799649.578461],
    [1765412.571200, 6815568.948682],
    [1770297.986514, 6826517.029677],
    [1776629.873093, 6843380.927495],
    [1781730.483219, 6860698.770212],
    [1785564.913315, 6878381.734597],
    [1787403.307999, 6890327.963135],
    [1789066.740586, 6908413.948228],
    [1789401.863030, 6926614.329268],
    [1788397.828327, 6944831.101876],
    [1786982.391807, 6956935.664644],
    [1783741.981466, 6974958.334189],
    [1780840.680040, 6986840.512587],
    [1775388.463150, 7004389.847986],
    [1771029.522059, 7015863.493182],
    [1763425.836193, 7032661.540391],
    [1757661.563536, 7043542.959008],
    [1748003.042117, 7059317.940417],
    [1740909.941657, 7069429.261434],
    [1729329.297108, 7083920.746542],
    [1721007.651557, 7093093.428164],
    [1712227.817031, 7101846.228703],
    [1703010.895068, 7110155.184783],
    [1688415.431598, 7121736.644159],
    [1678206.807166, 7128838.378866],
    [1662240.283419, 7138512.511743],
    [1651199.518817, 7144283.164919],
    [1634114.323672, 7151880.190618],
    [1622417.672168, 7156217.904465],
    [1610511.143269, 7159959.351883],
    [1594304.341095, 7163997.006185],
    [1596622.654464, 7180542.572031],
    [1597641.758407, 7193053.328242],
    [1598049.131569, 7205618.953498],
    [1597840.866303, 7218209.463254],
    [1596369.187337, 7237075.455858],
    [1594614.361610, 7249594.883074],
    [1592241.895641, 7262031.973929],
    [1589254.678031, 7274355.748733],
    [1583631.410618, 7292561.176140],
    [1579130.380710, 7304466.279948],
    [1574037.277770, 7316150.135281],
    [1568362.220278, 7327582.407500],
    [1558784.596330, 7344193.726370],
    [1551709.555742, 7354867.126525],
    [1544100.701955, 7365186.475218],
    [1535975.368664, 7375123.948342],
    [1527352.435580, 7384652.544351],
    [1518252.293131, 7393746.180416],
    [1503755.517873, 7406516.546475],
    [1488314.239319, 7418172.266883],
    [1472014.898805, 7428639.791683],
    [1454950.160042, 7437852.511888],
    [1437218.318281, 7445751.363990],
    [1418922.639921, 7452285.378025],
    [1400170.638875, 7457412.159658],
    [1387470.480282, 7460031.338831],
    [1374650.354138, 7462002.428074],
    [1361744.241891, 7463319.688831],
    [1348786.419621, 7463979.281288],
    [1335811.340913, 7463979.281288],
    [1322853.518642, 7463319.688831],
    [1309947.406395, 7462002.428074],
    [1297127.280251, 7460031.338831],
    [1284427.121658, 7457412.159658],
    [1271880.502098, 7454152.502656],
    [1259520.470243, 7450261.820216],
    [1247379.442252, 7445751.363990],
    [1235489.095838, 7440634.136432],
    [1223880.268683, 7434924.835315],
    [1212582.861728, 7428639.791683],
    [1196329.987602, 7418203.793754],
    [1188530.447060, 7420595.316586],
    [1176037.767077, 7423840.316253],
    [1157022.698122, 7427509.857553],
    [1137777.055637, 7429721.236010],
    [1118415.680731, 7430459.975898],
    [1099054.305826, 7429721.236010],
    [1079808.663341, 7427509.857553],
    [1060793.594386, 7423840.316253],
    [1042122.169647, 7418736.579578],
    [1023904.834369, 7412231.871856],
    [1012065.221149, 7407137.545905],
    [1000505.760272, 7401453.744248],
    [989256.213450, 7395196.708300],
    [978345.321041, 7388384.236715],
    [967800.716950, 7381035.612331],
    [957648.849596, 7373171.524169],
    [947914.909237, 7364813.985102],
    [938622.761897, 7355986.245760],
    [929794.890027, 7346712.705311],
    [921452.340040, 7337018.819692],
    [913614.676731, 7326931.007922],
    [906299.944593, 7316476.557054],
    [895747.962722, 7299073.458144],
    [884415.752181, 7306588.744197],
    [873450.706141, 7313052.145730],
    [856411.283203, 7321695.087509],
    [844699.628008, 7326731.687379],
    [826679.879268, 7333162.527225],
    [814410.892804, 7336682.412206],
    [801974.707649, 7339576.322742],
    [789404.077268, 7341835.890328],
    [770368.788860, 7344022.032681],
    [757608.056689, 7344671.153171],
    [744830.367614, 7344671.153171],
    [732069.635443, 7344022.032681],
    [719359.716123, 7342725.677633],
    [700463.716654, 7339576.322742],
    [688027.531499, 7336682.412206],
    [669696.835370, 7331170.948856],
    [657738.796295, 7326731.687379],
    [640273.218950, 7318957.226188],
    [628987.718163, 7313052.145730],
    [612669.072917, 7303153.333675],
    [602236.620248, 7295887.203835],
    [587323.271433, 7284038.629655],
    [577907.119849, 7275538.782193],
    [564629.342767, 7261946.595846],
    [556372.693288, 7252359.791139],
    [544928.951047, 7237256.739957],
    [537952.792153, 7226745.715391],
    [528506.984503, 7210385.809967],
    [522908.584836, 7199125.651895],
    [515588.271552, 7181778.669740],
    [511440.311503, 7169953.142743],
    [506335.866723, 7151899.159780],
    [502588.832169, 7133557.711901],
    [500210.418512, 7115031.742990],
    [499204.061465, 7096423.798008],
    [499293.668584, 7084023.172831],
    [499988.334377, 7071661.026793],
    [502154.659346, 7053254.092463],
    [505651.110094, 7035096.972664],
    [510450.126901, 7017284.285702],
    [514356.618066, 7005645.871976],
    [518815.519379, 6994227.614631],
    [526511.558481, 6977568.779198],
    [535374.243412, 6961545.768796],
    [545350.926626, 6946237.525232],
    [552592.736167, 6936466.119513],
    [560286.044889, 6927066.562106],
    [572630.811782, 6913707.947438],
    [585881.171553, 6901294.215660],
    [599966.120759, 6889883.744274],
    [609783.188534, 6882860.610886],
    [617408.632690, 6877897.228727],
    [610838.432880, 6871930.623086],
    [602298.802027, 6863432.548635],
    [590327.923291, 6849926.873396],
    [579419.356756, 6835584.208949],
    [569629.641218, 6820490.811444],
    [561008.018188, 6804736.475390],
    [553596.269758, 6788413.871429],
    [547428.621096, 6771617.888831],
    [544021.485667, 6760205.168822],
    [541185.294658, 6748653.750378],
    [538924.578827, 6736992.578550],
    [536618.806172, 6719358.398908],
    [535618.570194, 6701639.950006],
    [535920.841700, 6683933.937928],
    [537515.579919, 6666335.756996],
    [540385.966492, 6648939.005290],
    [544508.666970, 6631835.034919],
    [548959.808385, 6617692.569177],
    [538294.317474, 6603704.654860],
    [528761.796122, 6589041.810879],
    [520362.499098, 6573734.204865],
    [513137.379011, 6557871.570503],
    [507120.094736, 6541545.854836],
    [502336.966506, 6524850.596978],
    [498806.985840, 6507880.321926],
    [497155.944009, 6496460.894946],
    [496068.914282, 6484989.538220],
    [495546.199853, 6473494.255965],
    [495586.729480, 6462002.880037],
    [496188.082696, 6450543.004012],
    [498133.118070, 6433471.856514],
    [500117.346154, 6422209.326194],
    [504107.737852, 6405558.573350],
    [507431.776086, 6394657.499090],
    [513388.889453, 6378667.363262],
    [520477.199548, 6363183.205778],
    [528653.537314, 6348283.069783],
    [537869.753010, 6334041.246920],
    [548073.043012, 6320528.014592],
    [559206.281102, 6307809.400985],
    [571208.352233, 6295946.976196],
    [584014.487100, 6284997.667750],
    [597556.596222, 6275013.598782],
    [611763.602518, 6266041.947191],
    [626561.771694, 6258124.824125],
    [641875.039995, 6251299.170285],
    [657625.339126, 6245596.668625],
    [673732.918321, 6241043.672192],
    [690116.663754, 6237661.146009],
    [706694.415598, 6235464.622061],
    [721506.511561, 6234531.618212],
    [715535.368884, 6227265.446270],
    [708872.152614, 6218322.648495],
    [702667.979186, 6209067.909289],
    [696936.931062, 6199525.496161],
    [691691.761313, 6189720.266554],
    [686943.875248, 6179677.589952],
    [682703.316967, 6169423.270245],
    [678978.760670, 6158983.468692],
    [675777.506565, 6148384.627794],
    [673105.481176, 6137653.396356],
    [670967.241835, 6126816.555992],
    [669365.985138, 6115900.949321],
    [668303.559136, 6104933.410032],
    [667780.479013, 6093940.695024],
    [667795.946015, 6082949.418740],
    [668347.869378, 6071985.989849],
    [669432.891009, 6061076.550349],
    [671046.412692, 6050246.917189],
    [673182.625562, 6039522.526453],
    [675834.541637, 6028928.380141],
    [678994.027180, 6018488.995566],
    [682651.837677, 6008228.357362],
    [686797.654240, 5998169.872087],
    [691420.121238, 5988336.325387],
    [696506.884989, 5978749.841687],
    [702044.633343, 5969431.846343],
    [708019.136014, 5960403.030205],
    [714415.285511, 5951683.316516],
    [721217.138562, 5943291.830072],
    [728407.957900, 5935246.868571],
    [735970.254335, 5927565.876060],
    [743885.829001, 5920265.418406],
    [756380.145889, 5910062.250726],
    [765095.021238, 5903779.643946],
    [774094.502016, 5897928.327188],
    [788081.578286, 5889986.791613],
    [797700.244111, 5885265.628085],
    [807528.483886, 5881014.696943],
    [822614.806829, 5875539.116032],
    [832868.477971, 5872500.347526],
    [843252.135882, 5869958.379204],
    [853742.454631, 5867918.474260],
    [864315.923373, 5866384.849829],
    [874948.887547, 5865360.672587],
    [885617.590002, 5864848.055463],
    [896298.212053, 5864848.055463],
    [906966.914507, 5865360.672587],
    [917599.878682, 5866384.849829],
    [928173.347424, 5867918.474260],
    [938663.666173, 5869958.379204],
    [949047.324083, 5872500.347526],
    [964371.857130, 5877242.870920],
    [974387.318168, 5881014.696943],
    [984215.557944, 5885265.628085],
    [993834.223769, 5889986.791613],
    [1003221.334719, 5895168.303361],
    [1012355.323531, 5900799.278967],
    [1021215.078637, 5906867.846524],
    [1034565.268699, 5917294.241297],
  ]),
);

/*
 * Who draws where. Every palette here is part transparent, so wherever two
 * networks' colours land on one pixel they blend into a third intensity
 * neither measured; these shapes make sure exactly one does.
 *
 *   - Inside a network's own borders, only that network draws: it is the
 *     national radar for that ground.
 *   - Elsewhere the nearest radar that reaches wins, whatever network it is,
 *     DWD's sites reaching their 150 km outline. Rotterdam is drawn from
 *     Belgium's Helchteren at 114 km rather than France's Avesnes at 205, and
 *     Vienna from Slovakia's Malý Javorník at 58 rather than Czechia's
 *     Skalky at 147; the seams are where two radars are equally far, so
 *     their beams equally high.
 *
 * Every layer is WebGL and cannot be clipped, so the cuts are made in the
 * tile images: each EUMETNET layer's tiles are cut to its `*ExclusiveCoverage`
 * (`network.ts`), and each network's ground within DWD's sweeps, inside its
 * borders and beyond them, is erased from DWD's observation tiles
 * (`networkHoles.ts`). Every ring is wound so the nonzero rule treats holes as
 * holes, in EPSG:3857, the projection the map renders in.
 *
 * The shapes are meant to be the server's: the backend decides by them which
 * domain a point belongs to (a phone's alerts, once there is more than DWD's
 * forecast) and serves them at `/v3/radar/coverage`.
 * `spec/coverage.json` is the backend's document, copied byte for byte; copy
 * it again whenever a network or a site's reach changes there.
 *
 * A coverage may be several rings: islands of its own, and holes where
 * another country's islands lie in the sea it draws over (the North Frisian
 * Islands and Bohuslän's skerries in Denmark's). Networks no DWD radar
 * reaches (Lithuania, Estonia, Ireland, Finland, Norway) have an empty
 * `dwd_hole`.
 */
import coverage from "../../spec/coverage.json" with { type: "json" };

type Rings = number[][][];

/** Where the Swiss layer may draw: Switzerland, plus the ground beyond every border its radars are nearest to. */
export const chExclusiveCoverage: Rings = coverage.domains.ch.exclusive;
/** Where the French layer may draw. */
export const frExclusiveCoverage: Rings = coverage.domains.fr.exclusive;
/** Where the Czech layer may draw. */
export const czExclusiveCoverage: Rings = coverage.domains.cz.exclusive;
/** Where the Polish layer may draw. */
export const plExclusiveCoverage: Rings = coverage.domains.pl.exclusive;
/** Where the Belgian layer may draw. */
export const beExclusiveCoverage: Rings = coverage.domains.be.exclusive;
/** Where the Slovak layer may draw. */
export const skExclusiveCoverage: Rings = coverage.domains.sk.exclusive;

/**
 * Switzerland, and the ground beyond it the Swiss radars are nearest to, as
 * a hole in DWD's observation tiles: the part DWD's tiles can carry data
 * over at all, within 180 km of DWD's radars, the reach of its sweeps.
 */
export const chBorders: Rings = coverage.domains.ch.dwd_hole;
/**
 * France and the ground its radars are nearest to, as a hole in DWD's
 * observation tiles, cut the same way: erasing the rest of France from them
 * would cost thousands of vertices and erase nothing.
 */
export const frBordersNearDwd: Rings = coverage.domains.fr.dwd_hole;
/** Czechia and the ground its radars are nearest to, cut the same way. */
export const czBordersNearDwd: Rings = coverage.domains.cz.dwd_hole;
/** Poland and the ground its radars are nearest to, cut the same way. */
export const plBordersNearDwd: Rings = coverage.domains.pl.dwd_hole;
/** Belgium and the ground its radars are nearest to, cut the same way. */
export const beBordersNearDwd: Rings = coverage.domains.be.dwd_hole;
/**
 * Slovakia's, cut the same way: none of DWD's radars reaches Slovakia, so
 * only a corner of Austria south of Linz, where Malý Javorník is the
 * nearest radar and DWD's Isen still sweeps.
 */
export const skBordersNearDwd: Rings = coverage.domains.sk.dwd_hole;
/** Where the Lithuanian layer may draw: Lithuania, and most of Latvia and western Belarus beyond it. */
export const ltExclusiveCoverage: Rings = coverage.domains.lt.exclusive;
/** Where the Estonian layer may draw: Estonia, and northern Latvia and the Pskov oblast beyond it. */
export const eeExclusiveCoverage: Rings = coverage.domains.ee.exclusive;
/** Where the Irish layer may draw: Ireland, and Northern Ireland and the Irish Sea's coasts beyond it. */
export const ieExclusiveCoverage: Rings = coverage.domains.ie.exclusive;
/** Where the Croatian layer may draw: Croatia, and Slovenia, Bosnia and northern Serbia beyond it. */
export const hrExclusiveCoverage: Rings = coverage.domains.hr.exclusive;
/** Where the Danish layer may draw, its islands each a ring of their own. */
export const dkExclusiveCoverage: Rings = coverage.domains.dk.exclusive;
/** Where the Dutch layer may draw. */
export const nlExclusiveCoverage: Rings = coverage.domains.nl.exclusive;
/** Where the Finnish layer may draw: Finland, and Karelia and St Petersburg beyond it. */
export const fiExclusiveCoverage: Rings = coverage.domains.fi.exclusive;
/** Where the Swedish layer may draw. */
export const seExclusiveCoverage: Rings = coverage.domains.se.exclusive;
/** Where the Norwegian layer may draw: Norway, and the Kola peninsula beyond it. */
export const noExclusiveCoverage: Rings = coverage.domains.no.exclusive;

/** Lithuania's, empty: no DWD radar sweeps it or any ground its radars are nearest to. */
export const ltBordersNearDwd: Rings = coverage.domains.lt.dwd_hole;
/** Estonia's, empty, for the same reason. */
export const eeBordersNearDwd: Rings = coverage.domains.ee.dwd_hole;
/** Ireland's, empty, for the same reason. */
export const ieBordersNearDwd: Rings = coverage.domains.ie.dwd_hole;
/** Croatia's: the ground in Carinthia and Salzburg its radars are nearest to and DWD's Isen still sweeps. */
export const hrBordersNearDwd: Rings = coverage.domains.hr.dwd_hole;
/** Denmark and the sea its radars are nearest to, within DWD's sweeps along Germany's northern coast. */
export const dkBordersNearDwd: Rings = coverage.domains.dk.dwd_hole;
/** The Netherlands, within DWD's sweeps from Borkum and Essen. */
export const nlBordersNearDwd: Rings = coverage.domains.nl.dwd_hole;
/** Finland's, empty, for the same reason as Lithuania's. */
export const fiBordersNearDwd: Rings = coverage.domains.fi.dwd_hole;
/** Sweden's: southern Skåne, within DWD's sweeps from Rostock. */
export const seBordersNearDwd: Rings = coverage.domains.se.dwd_hole;
/** Norway's, empty, for the same reason as Lithuania's. */
export const noBordersNearDwd: Rings = coverage.domains.no.dwd_hole;
